import { useChat } from '@ai-sdk/react';
import { applySpecPatch, parseSpecStreamLine } from '@json-render/core';
import { type DataPart, buildSpecFromParts } from '@json-render/react';
import { DefaultChatTransport, UIMessage } from 'ai';
import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatest, useMount } from 'react-use';

import { useApiClient } from '@eluve/api-client-provider';
import type {
  ChatContextData,
  EluveChatDataTypes,
  UserContextDataPart,
} from '@eluve/api-contract';
import { getEndpointUrl } from '@eluve/api-contract';
import {
  type ChatThread,
  type Citation,
  type PdfContextState,
  type StartingChatMessage,
  type ToolInvocation,
  useChatFileManager,
} from '@eluve/blocks';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import type { Spec } from '@eluve/sdui-types';
import { useUserIdFromSession } from '@eluve/session-helpers';

// ---------------------------------------------------------------------------
// Spec-from-text parser
// ---------------------------------------------------------------------------

/**
 * Parse spec patches from text content that contains ```spec fenced blocks.
 * Mastra stores messages before pipeJsonRender transforms them, so historical
 * messages have raw spec patches embedded in the text content.
 */
function parseSpecFromText(text: string): {
  cleanText: string;
  spec: Spec | null;
} {
  const lines = text.split('\n');
  const cleanLines: string[] = [];
  const spec: Spec = { root: '', elements: {} };
  let inSpecFence = false;
  let hasPatches = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!inSpecFence && trimmed.startsWith('```spec')) {
      inSpecFence = true;
      continue;
    }
    if (inSpecFence && trimmed === '```') {
      inSpecFence = false;
      continue;
    }
    if (inSpecFence) {
      const patch = parseSpecStreamLine(trimmed);
      if (patch) {
        hasPatches = true;
        applySpecPatch(spec, patch);
      }
      continue;
    }
    cleanLines.push(line);
  }

  return {
    cleanText: cleanLines.join('\n').trim(),
    spec: hasPatches ? spec : null,
  };
}

// ---------------------------------------------------------------------------
// Sub-agent tool invocation extraction
// ---------------------------------------------------------------------------

interface SubAgentToolResult {
  toolCallId: string;
  toolName: string;
  result: unknown;
  args?: unknown;
}

interface DataToolAgentPayload {
  toolResults?: SubAgentToolResult[];
}

function extractSubAgentToolInvocations(
  parts: Array<{ type: string }>,
): ToolInvocation[] {
  return parts
    .filter((p) => p.type === 'data-tool-agent')
    .flatMap((p) => {
      const agentData = (p as unknown as { data: DataToolAgentPayload }).data;
      return (agentData?.toolResults ?? []).map(
        (tr) =>
          ({
            toolCallId: tr.toolCallId,
            toolName: tr.toolName,
            state: 'output-available',
            output: tr.result,
          }) satisfies ToolInvocation,
      );
    });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessageMetadata {
  citations?: Citation[];
}

type UIMessageRole = 'user' | 'system' | 'assistant';

interface ApiToolInvocation {
  toolCallId: string;
  toolName: string;
  state: string;
  input?: unknown;
  output?: unknown;
}

interface ApiMessageWithAnnotations {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system' | 'data';
  annotations?: ChatMessageMetadata[];
  toolInvocations?: ApiToolInvocation[];
  contexts?: ChatContextData[];
}

export type ChatView = 'new' | 'history' | 'thread';

export type ChatSessionInput = {
  tenantId: string;
  baseUrl: string;
  patientId?: string;
  appointmentId?: string;
  startingMessages: StartingChatMessage[];
  /** Custom headers for the SSE transport (e.g. Bearer auth in extension) */
  transportHeaders?:
    | Record<string, string>
    | Headers
    | (() => Promise<Record<string, string> | Headers>);
  /** Credentials mode for the SSE transport (defaults to 'include') */
  transportCredentials?:
    | RequestCredentials
    | (() => Promise<RequestCredentials>);
  /** When set, automatically sends this message once on mount. */
  initialMessage?: string;
  /** When provided alongside initialMessage, identifies which conversation starter triggered the auto-send. */
  initialStarterId?: string;
  /** When provided alongside initialMessage, identifies the preference associated with the starter. */
  initialPreferenceId?: string;
  /** When provided alongside initialMessage, identifies the spec template associated with the starter. */
  initialSpecTemplateId?: string;
  /** When set, opens the chat to this view on mount. Defaults to 'new'. */
  initialView?: 'new' | 'history';
  /** Pre-populates `selectedContext` on mount only — changing it later has
   * no effect; consumers should call `setSelectedContext` directly. */
  initialContext?: ChatContextData;

  // Callbacks (consumers use these to react to side-effects)
  onStartersChanged?: () => void;
  onNoteUpdated?: () => void;
  onCustomInstructionSaved?: () => void;
  onDocumentAdded?: (doc?: { id: string; title: string }) => void;
};

export type ChatSessionResult = {
  // State
  threads: ChatThread[];
  currentThreadId: string | null;
  view: ChatView;
  draftMessage: string;
  status: 'submitted' | 'streaming' | 'ready' | 'error' | undefined;
  startingMessages: StartingChatMessage[];
  hasMore: boolean;
  isLoadingMore: boolean;

  // Actions
  sendMessage: (
    content?: string,
    preferenceId?: string,
    starterId?: string,
    specTemplateId?: string,
  ) => Promise<void>;
  stopStream: () => void;
  startNewChat: () => void;
  goToHistory: () => void;
  selectThread: (threadId: string) => void;
  updateThreadTitle: (threadId: string, newTitle: string) => Promise<void>;
  setDraftMessage: (value: string) => void;
  ensureThread: () => Promise<string | null>;
  loadMore: () => Promise<void>;

  // PDF manager passed through; consumers render PDF preview if they want
  pdfManager: PdfContextState;

  // Source click handler (for citation clicks in messages)
  handleSourceClick: (citation: Citation) => void;

  selectedContext: ChatContextData | null;
  setSelectedContext: (ctx: ChatContextData) => void;
  clearSelectedContext: () => void;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useChatSession = (input: ChatSessionInput): ChatSessionResult => {
  const {
    tenantId,
    baseUrl,
    patientId,
    appointmentId,
    startingMessages,
    transportHeaders,
    transportCredentials,
    initialMessage,
    initialStarterId,
    initialPreferenceId,
    initialSpecTemplateId,
    initialView,
  } = input;

  const logger = useNamedLogger('chat');
  const analytics = useAnalytics('chat');
  const userId = useUserIdFromSession();
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [view, setView] = useState<ChatView>(initialView ?? 'new');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const latestThreadRef = useLatest(currentThreadId);
  const apiClient = useApiClient();

  const createThread = useCallback(async () => {
    const result = await apiClient.mastra.createChatThread({
      params: { tenantId },
      body: patientId ? { patientId } : {},
    });

    if (result.status === 201) {
      const { threadId } = result.body;
      setCurrentThreadId(threadId);

      const newThread: ChatThread = {
        id: threadId,
        title: `New Thread ${new Date().toISOString()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return { threadId, newThread };
    }
    return null;
  }, [apiClient, tenantId, patientId]);

  const fetchThreads = useCallback(
    async (pageNum: number) => {
      const result = await apiClient.mastra.getChatThreads({
        params: { tenantId },
        query: {
          ...(patientId && { patientId }),
          page: pageNum,
          perPage: 20,
        },
      });

      if (result.status === 200) {
        const { threads, hasMore: more, page: responsePage } = result.body;

        if (pageNum === 1) {
          setChatThreads(threads);
        } else {
          setChatThreads((prev) => {
            const existingIds = new Set(prev.map((t) => t.id));
            const newThreads = threads.filter((t) => !existingIds.has(t.id));
            return [...prev, ...newThreads];
          });
        }

        setHasMore(more);
        setPage(responsePage);
        return threads;
      }
      return null;
    },
    [apiClient, tenantId, patientId],
  );

  useMount(async () => {
    await fetchThreads(1);
  });

  const loadThreadTitle = useCallback(
    async (threadId: string) => {
      const result = await apiClient.mastra.getChatThreadTitle({
        params: { tenantId, threadId },
      });

      if (result.status === 200) {
        const { title } = result.body;
        setChatThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId ? { ...thread, title } : thread,
          ),
        );
      }
    },
    [apiClient, tenantId],
  );

  const [draftMessage, setDraftMessage] = useState('');
  const [selectedContext, setSelectedContextState] =
    useState<ChatContextData | null>(input.initialContext ?? null);
  const setSelectedContext = useCallback((ctx: ChatContextData) => {
    setSelectedContextState(ctx);
  }, []);
  const clearSelectedContext = useCallback(() => {
    setSelectedContextState(null);
  }, []);

  // Refs to ensure async callbacks (onFinish, prepareSendMessagesRequest) read latest values
  const currentThreadIdRef = useRef(currentThreadId);
  currentThreadIdRef.current = currentThreadId;
  const messagesRef = useRef<typeof messages>([]);

  // Store preferenceId, starterId, and specTemplateId from conversation starter clicks
  const pendingPreferenceIdRef = useRef<string | undefined>(undefined);
  const pendingStarterIdRef = useRef<string | undefined>(undefined);
  const pendingSpecTemplateIdRef = useRef<string | undefined>(undefined);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: getEndpointUrl(baseUrl, 'chatInsightsWithCitations', {
          params: { tenantId },
        }),
        credentials: transportCredentials ?? 'include',
        ...(transportHeaders && { headers: transportHeaders }),
        prepareSendMessagesRequest: ({ messages: chatMessages }) => {
          const lastMessage =
            chatMessages.length > 0
              ? chatMessages[chatMessages.length - 1]
              : null;

          // Send both: `parts` for the backend, `content` for back-compat callers.
          const messageContent = lastMessage
            ? {
                content: lastMessage.parts
                  .filter(
                    (p): p is { type: 'text'; text: string } =>
                      p.type === 'text',
                  )
                  .map((p) => p.text)
                  .join(''),
                parts: lastMessage.parts,
              }
            : null;

          const preferenceId = pendingPreferenceIdRef.current;
          pendingPreferenceIdRef.current = undefined;
          const starterId = pendingStarterIdRef.current;
          pendingStarterIdRef.current = undefined;
          const specTemplateId = pendingSpecTemplateIdRef.current;
          pendingSpecTemplateIdRef.current = undefined;

          return {
            body: {
              message: messageContent,
              threadId: currentThreadIdRef.current,
              ...(patientId && { patientId }),
              ...(appointmentId && { appointmentId }),
              ...(preferenceId && { preferenceId }),
              ...(starterId && { starterId }),
              ...(specTemplateId && { specTemplateId }),
            },
          };
        },
      }),
    [
      baseUrl,
      tenantId,
      patientId,
      appointmentId,
      transportHeaders,
      transportCredentials,
    ],
  );

  const {
    messages,
    status,
    setMessages,
    sendMessage: aiSendMessage,
    stop,
  } = useChat<UIMessage<ChatMessageMetadata, EluveChatDataTypes>>({
    transport,
    experimental_throttle: 50,
    onFinish: async () => {
      const threadId = currentThreadIdRef.current;
      if (!threadId) {
        return;
      }
      // Auto generate title after first complete exchange
      if (messagesRef.current.length < 2) {
        try {
          await apiClient.mastra.updateThreadTitle({
            params: { tenantId, threadId },
            query: patientId ? { patientId } : {},
            body: { autoGenerate: true },
          });
        } catch (error) {
          logger.warn('Failed to auto-generate thread title:', {
            threadId,
            error,
          });
        }
      }
      loadThreadTitle(threadId);
    },
  });

  messagesRef.current = messages;

  const loadThreadMessages = async (threadId: string) => {
    const result = await apiClient.mastra.getChatThreadMessages({
      params: { tenantId, threadId },
      query: {
        includeSources: 'true',
        ...(patientId && { patientId }),
      },
    });
    if (latestThreadRef.current !== threadId) return;
    if (result.status === 200) {
      const messagesWithAnnotations = result.body
        .messages as unknown as ApiMessageWithAnnotations[];
      const uiMessages = messagesWithAnnotations
        .filter(
          (msg): msg is ApiMessageWithAnnotations & { role: UIMessageRole } =>
            msg.role !== 'data',
        )
        .map((msg) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const parts: any[] = [];

          if (msg.contexts?.length) {
            for (const ctx of msg.contexts) {
              parts.push({ type: 'data-userContext', data: ctx });
            }
          }

          parts.push({ type: 'text' as const, text: msg.content || '' });

          // Reconstruct tool invocation parts so cards render for historical messages
          if (msg.toolInvocations) {
            for (const inv of msg.toolInvocations) {
              parts.push({
                type: `tool-${inv.toolName}`,
                toolCallId: inv.toolCallId,
                state: inv.state,
                input: inv.input,
                output: inv.output,
              });
            }
          }

          return {
            id: msg.id,
            role: msg.role,
            parts,
            metadata: (msg.annotations?.[0] ?? {}) as ChatMessageMetadata,
          };
        });
      setMessages(uiMessages);
    } else {
      logger.warn('Failed to load thread messages', { threadId });
    }
  };

  const formattedMessages = useMemo(
    () =>
      messages.map((msg) => {
        // Extract text content from parts array
        const rawContent = msg.parts
          .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
          .map((p) => p.text)
          .join('');

        const toolInvocations = msg.parts
          .filter((p) => p.type.startsWith('tool-'))
          .map((p) => {
            const toolPart = p as {
              type: string;
              toolCallId: string;
              state: ToolInvocation['state'];
              input?: unknown;
              output?: unknown;
            };
            return {
              toolCallId: toolPart.toolCallId,
              toolName: toolPart.type.replace(/^tool-/, ''),
              state: toolPart.state,
              input: toolPart.input,
              output: toolPart.output,
            } satisfies ToolInvocation;
          });

        const subAgentToolResults = extractSubAgentToolInvocations(msg.parts);

        const allToolInvocations = [...toolInvocations, ...subAgentToolResults];

        // Extract SDUI spec from message parts (streaming messages have DataParts)
        // Deep clone parts because buildSpecFromParts mutates values in-place via
        // JSON Patch splice operations, but msg.parts is frozen React state.
        // Only clone when parts contain DataParts that buildSpecFromParts
        // would mutate via JSON Patch splice operations.
        const hasDataParts = msg.parts.some((p) => p.type.startsWith('data'));
        const parts = hasDataParts ? structuredClone(msg.parts) : msg.parts;
        let spec = buildSpecFromParts(parts as DataPart[]);
        let content = rawContent;

        // For historical messages loaded from API, spec patches are embedded in the
        // text as ```spec fenced blocks. Parse them and strip from display content.
        if (!spec && rawContent.includes('```spec')) {
          const parsed = parseSpecFromText(rawContent);
          spec = parsed.spec;
          content = parsed.cleanText;
        }

        // Extract citations from metadata
        const citations = msg.metadata?.citations ?? [];

        const contexts = msg.parts
          .filter(
            (p): p is UserContextDataPart => p.type === 'data-userContext',
          )
          .map((p) => p.data);

        return {
          id: msg.id,
          content,
          role:
            msg.role === 'user'
              ? 'user'
              : ('assistant' as 'user' | 'assistant'),
          citations,
          ...(allToolInvocations.length > 0 && {
            toolInvocations: allToolInvocations,
          }),
          ...(contexts.length > 0 && { contexts }),
          ...(spec && { spec }),
        };
      }),
    [messages],
  );

  const consolidatedChatThreads = useMemo(
    () =>
      produce(chatThreads, (draft) => {
        if (!currentThreadId) return;

        const existingThread = draft.find((t) => t.id === currentThreadId);
        if (existingThread) {
          existingThread.messages = formattedMessages;
        } else {
          draft.unshift({
            id: currentThreadId,
            title: `New Thread ${new Date().toISOString()}`,
            messages: formattedMessages,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
      }),
    [chatThreads, currentThreadId, formattedMessages],
  );

  const handleNewChat = () => {
    if (status === 'streaming' || status === 'submitted') {
      stop();
    }

    analytics.capture('chat_new_thread_clicked', {
      tenantId,
      userId,
      patientId,
      previousThreadId: currentThreadId,
    });

    setCurrentThreadId(null);
    setMessages([]);
    setView('new');
  };

  const isCreatingThreadRef = useRef(false);

  const ensureThread = useCallback(async (): Promise<string | null> => {
    if (currentThreadIdRef.current) return currentThreadIdRef.current;
    if (isCreatingThreadRef.current) return null;
    isCreatingThreadRef.current = true;
    try {
      const result = await createThread();
      if (!result) return null;
      currentThreadIdRef.current = result.threadId;
      setChatThreads((prev) => [result.newThread, ...prev]);
      setView('thread');
      return result.threadId;
    } finally {
      isCreatingThreadRef.current = false;
    }
  }, [createThread]);

  const handleMessageSend = async (
    content?: string,
    preferenceId?: string,
    starterId?: string,
    specTemplateId?: string,
  ) => {
    if (starterId) {
      const starter = startingMessages.find((s) => s.id === starterId);
      analytics.capture('chat_conversation_starter_clicked', {
        starterId,
        starterTitle: starter?.title,
        tenantId,
        userId,
        appointmentId,
      });
    }

    const messageText = content ?? draftMessage;
    if (messageText.trim()) {
      const wasNewThread = currentThreadIdRef.current === null;
      const threadId = await ensureThread();
      if (!threadId) return;

      if (wasNewThread) {
        analytics.capture('chat_first_message_sent', {
          tenantId,
          userId,
          patientId,
          appointmentId,
          source: starterId ? 'starter' : 'typed',
          starterId,
          preferenceId,
        });
      }

      pendingPreferenceIdRef.current = preferenceId;
      pendingStarterIdRef.current = starterId;
      pendingSpecTemplateIdRef.current = specTemplateId;

      if (selectedContext) {
        aiSendMessage({
          parts: [
            { type: 'data-userContext', data: selectedContext },
            { type: 'text', text: messageText },
          ],
        });
        setSelectedContextState(null);
      } else {
        aiSendMessage({ text: messageText });
      }
      setDraftMessage('');
    }
  };

  const handleThreadTitleUpdate = async (
    threadId: string,
    newTitle: string,
  ) => {
    newTitle = newTitle.trim();

    if (!threadId || !newTitle) {
      return;
    }

    const thread = chatThreads.find((t) => t.id === threadId);
    const previousTitle = thread?.title ?? '';

    try {
      await apiClient.mastra.updateThreadTitle({
        params: { tenantId, threadId },
        query: patientId ? { patientId } : {},
        body: { title: newTitle },
      });

      analytics.capture('chat_thread_renamed', {
        tenantId,
        userId,
        patientId,
        threadId,
        previousTitle,
        newTitle,
      });

      setChatThreads((prev) =>
        prev.map((thread) =>
          thread.id === threadId ? { ...thread, title: newTitle } : thread,
        ),
      );
    } catch (error) {
      logger.warn('Failed to update thread title:', {
        threadId,
        error,
      });
    }
  };

  const handleLoadMore = async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      await fetchThreads(page + 1);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleGoToHistory = () => {
    if (status === 'streaming' || status === 'submitted') {
      stop();
    }

    analytics.capture('chat_history_clicked', {
      tenantId,
      userId,
      patientId,
      currentThreadId,
    });

    setCurrentThreadId(null);
    setMessages([]);
    setView('history');
  };

  const handleSelectThread = (threadId: string) => {
    analytics.capture('chat_thread_selected', {
      tenantId,
      userId,
      patientId,
      threadId,
    });

    setCurrentThreadId(threadId);
    setMessages([]);
    setView('thread');
    loadThreadMessages(threadId);
  };

  // PDF manager
  const pdfManager = useChatFileManager({
    tenantId,
    patientId: patientId ?? '',
    currentThreadId,
  });

  // Source click handler
  const handleSourceClick = useCallback(
    (citation: Citation) => {
      analytics.capture('chat_source_clicked', {
        tenantId,
        userId,
        sourceType: citation.source.sourceType,
        sourceId: citation.source.sourceDocId,
        threadId: currentThreadId,
      });
    },
    [analytics, tenantId, userId, currentThreadId],
  );

  // Auto-send initialMessage on first mount
  const hasAutoSentRef = useRef(false);
  useEffect(() => {
    if (!initialMessage || hasAutoSentRef.current) return;
    hasAutoSentRef.current = true;
    void handleMessageSend(
      initialMessage,
      initialPreferenceId,
      initialStarterId,
      initialSpecTemplateId,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialMessage,
    initialPreferenceId,
    initialStarterId,
    initialSpecTemplateId,
  ]);

  return {
    // State
    threads: consolidatedChatThreads,
    currentThreadId,
    view,
    draftMessage,
    status,
    startingMessages,
    hasMore,
    isLoadingMore,

    // Actions
    sendMessage: handleMessageSend,
    stopStream: stop,
    startNewChat: handleNewChat,
    goToHistory: handleGoToHistory,
    selectThread: handleSelectThread,
    updateThreadTitle: handleThreadTitleUpdate,
    setDraftMessage,
    ensureThread,
    loadMore: handleLoadMore,

    // PDF manager
    pdfManager,

    // Source click
    handleSourceClick,

    selectedContext,
    setSelectedContext,
    clearSelectedContext,
  };
};
